import { ConditionType, filterOperatorOptions } from '@fabric-ui/components';
import { FieldType } from '../../views/Sources/Mapping/types';

export interface OperatorOption {
  value: string;
  title: string;
  [key: string]: string | number | boolean;
}

/**
 * Get operator options for from-to filter mode based on field type and condition type
 */
export const getFromToFilterOperatorOptions = (
  typeCondition: ConditionType,
  typeName: string,
  existingOperatorOptions?: typeof filterOperatorOptions
): OperatorOption[] => {
  // Default operators for all types - aligned with backend contract
  const defaultOperatorOptions: OperatorOption[] = [{ value: 'equals', title: 'Equals' }];

  // String-specific operators - aligned with backend contract
  const stringOperatorOptions: OperatorOption[] = [
    { value: 'contains', title: 'Contains' },
    { value: 'notContains', title: 'Does Not Contain' },
    { value: 'starts', title: 'Starts With' },
    { value: 'ends', title: 'Ends With' },
  ];

  // Date-specific operators for from-to filter mode
  const dateOperatorOptions: OperatorOption[] = [
    { value: 'equals', title: 'Equals' },
    { value: 'greaterThan', title: 'After' },
    { value: 'lessThan', title: 'Before' },
    { value: 'between', title: 'Between' },
  ];

  // Determine base operator options based on condition type
  const baseOperatorOptions: OperatorOption[] =
    typeCondition === ConditionType.string
      ? [...stringOperatorOptions, ...defaultOperatorOptions]
      : typeCondition === ConditionType.date
        ? dateOperatorOptions
        : defaultOperatorOptions;

  // Create a set of allowed operator values for from-to filter mode
  const allowedOperatorValues = new Set(baseOperatorOptions.map(opt => opt.value));

  // Use existing operator options if available, but filter to only include from-to filter compatible operators
  if (existingOperatorOptions && existingOperatorOptions[typeName]?.length > 0) {
    return existingOperatorOptions[typeName]
      .filter(opt => allowedOperatorValues.has(opt.value))
      .map(opt => ({ value: opt.value, title: opt.title }));
  }
  return baseOperatorOptions;
};

/**
 * Get default operator for from-to filter mode
 */
export const getDefaultFromToFilterOperator = (isDateField = false): string => (isDateField ? 'between' : 'equals');

/**
 * Check if a field is a date field
 */
export const isDateFieldType = (fieldName: string | null | undefined, fieldMap: Record<string, any>): boolean =>
  !!fieldName && [FieldType.Date, FieldType.DateTime].includes(fieldMap[fieldName]?.type as string);
